// frontend/src/hooks/useClusters.ts — the clusters read surface (list · detail · the
// per-cluster responsibility chains), mirroring useZones. Project-scoped server state
// (FRONTEND-PATTERNS §State): list + detail share the `['clusters', projectId, …]`
// prefix; the accountability chains live under their OWN key so a chain edit
// doesn't refetch the cluster detail's StatStrip.
//
// The list answers under a named rule — `manage` (the default; this hook's list: the
// admin page's two audiences, an admin or a vocabulary manager seeing every cluster, a
// people manager their own-scope clusters) · `reach` (a filter's source: any member,
// the clusters their scope reaches — the naming rule). The chains route additionally
// needs has_field_force — the consumer only mounts the chains query when the module is
// on. 401 is the centralized apiClient's job (ADR-003); errors bubble so the 5-state
// contract renders.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { ContainerListRule } from '@ems-app/schemas/zones/requests';
import type { ClusterDetail, ClusterListItem, ClustersListResponse } from '@ems-app/schemas/clusters/responses';
import { apiClient } from '../lib/apiClient.js';
import type { ParentedSource, SmallSource } from './useOptions.js';

export const clustersKeys = {
  /** List key (the `manage` rule — the admin page's list) — also the invalidation PREFIX (covers detail + reach). */
  all: (projectId: string) => ['clusters', projectId] as const,
  /** The list under the `reach` rule — a filter's source; under the prefix, so the cluster mutations invalidate it too. */
  reach: (projectId: string) => ['clusters', projectId, 'reach'] as const,
  detail: (projectId: string, clusterId: number) => ['clusters', projectId, clusterId] as const,
};

// ── list ────────────────────────────────────────────────────────────────────
async function fetchClusters(projectId: string, rule: ContainerListRule): Promise<ClustersListResponse> {
  const r = await apiClient.get<ClustersListResponse>(`/projects/${projectId}/clusters`, { params: { rule } });
  return r.data;
}

// Module-level selector (referentially stable, §State select doctrine).
const selectItems = (r: ClustersListResponse): ClusterListItem[] => r.items;

export function useClusters(projectId: string): UseQueryResult<ClusterListItem[], Error> {
  return useQuery({
    queryKey: clustersKeys.all(projectId),
    queryFn: () => fetchClusters(projectId, 'manage'),
    enabled: projectId.length > 0,
    select: selectItems,
  });
}

/** The clusters as a picker's source (ADR-080 §3 — the small kind): the WHOLE list on the press; each row carries its
 *  ZONE as its parent (a dependent list — a field on it declares `dependsOn`, ADR-080 §9); a disabled cluster is an
 *  inactive row. Under the `manage` rule (the default — a FORM on an admin page) it shares this hook's own list key;
 *  under `reach` (a FILTER — any member, the clusters their scope reaches: the naming rule) it keeps its own key under
 *  the same prefix. */
export function clustersSource(projectId: string, rule: ContainerListRule = 'manage'): ParentedSource<ClustersListResponse> {
  return {
    kind: 'small',
    queryKey: rule === 'reach' ? clustersKeys.reach(projectId) : clustersKeys.all(projectId),
    fetch: () => fetchClusters(projectId, rule),
    rows: (r) => r.items.map((c) => ({ id: String(c.id), label: c.name, description: c.zone_name, pickable: true, inactive: c.is_disabled, group: null, parent: String(c.zone_id) })),
    parented: true,
  };
}

/** The clusters the viewer's scope reaches (`reach`) as a hook — the site wizard's fresh-create pre-fill reads them
 *  at mount (a sole placeable cluster, the project's default when placeable); the picker shares the entry. */
export function useClustersReach(projectId: string): UseQueryResult<ClusterListItem[], Error> {
  return useQuery({
    queryKey: clustersKeys.reach(projectId),
    queryFn: () => fetchClusters(projectId, 'reach'),
    enabled: projectId.length > 0,
    staleTime: 60_000,
    select: selectItems,
  });
}

/** The site form's cluster field (ADR-080 §3 · §14): ONE picker over the clusters the viewer's scope reaches, each
 *  under its ZONE as a heading (`group` = the zone's name — the family's group headings, no tree, no boxes: the zone
 *  is not a choice, it follows the cluster) and found by its name or its zone's (the zone on the row's second line,
 *  matched as a code); the chosen reads "W3 · East". A cluster outside the viewer's own CLUSTER reach is offered
 *  greyed with its reason (`placeable` — the server refuses it on save all the same); a disabled one is inactive.
 *  Sorted by zone then name so each zone's rows run together under one heading. */
export function siteClusterSource(projectId: string, words: { outsideScope: string }): SmallSource<ClustersListResponse> {
  return {
    kind: 'small',
    queryKey: clustersKeys.reach(projectId),
    fetch: () => fetchClusters(projectId, 'reach'),
    rows: (r) =>
      [...r.items]
        .sort((a, b) => a.zone_name.localeCompare(b.zone_name) || a.name.localeCompare(b.name))
        .map((c) => ({
          id: String(c.id),
          label: c.name,
          description: c.placeable ? c.zone_name : `${c.zone_name} · ${words.outsideScope}`,
          selectedLabel: `${c.name} · ${c.zone_name}`,
          pickable: c.placeable,
          inactive: c.is_disabled,
          group: c.zone_name,
        })),
  };
}

// ── detail ────────────────────────────────────────────────────────────────────
/** The cluster's detail read. `quiet` — a form's Load the latest: a cluster gone meanwhile answers the form, never
 *  the not-found page (ADR-084 §6). */
export async function fetchCluster(projectId: string, clusterId: number, quiet = false): Promise<ClusterDetail> {
  const r = await apiClient.get<ClusterDetail>(`/projects/${projectId}/clusters/${clusterId}`, quiet ? { quietNotFound: true } : undefined);
  return r.data;
}

export function useCluster(
  projectId: string,
  clusterId: number | null,
): UseQueryResult<ClusterDetail, Error> {
  return useQuery({
    queryKey: clustersKeys.detail(projectId, clusterId ?? 0),
    queryFn: () => fetchCluster(projectId, clusterId as number),
    enabled: projectId.length > 0 && clusterId != null && clusterId > 0,
  });
}

